import { createContext, useContext, useEffect, useRef, useState } from 'react';
import config from '@/config';

const ReCaptchaContext = createContext();

export const ReCaptchaProvider = ({ children }) => {
  const [loaded, setLoaded] = useState(false);
  const intervalId = useRef(null);
  const recaptchaWidgetId = useRef(null);
  const [token, setToken] = useState(null);

  const sitekey = config.googleReCaptchaSiteKeyV2;

  const getCaptchaToken = async () => {
    if (token) return token;

    if (loaded && window.grecaptcha && recaptchaWidgetId.current !== null) {
      const newToken = await window.grecaptcha.execute(
        recaptchaWidgetId.current,
      );

      if (typeof newToken === 'string') {
        setToken(newToken);
        return newToken;
      }
    } else {
      console.warn('reCAPTCHA not ready or widget ID not found.');
      return null;
    }
  };

  useEffect(() => {
    if (!document) return;
    // Load the reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=explicit`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      intervalId.current = setInterval(() => {
        if (!window.grecaptcha?.render) return;
        clearInterval(intervalId.current);
        setLoaded(true);
      }, 1000);
    };
    script.onerror = () => {
      console.error('reCAPTCHA script load error');
      setLoaded(false);
      setToken(null);
      clearInterval(intervalId.current);
    };
    document.body.appendChild(script);

    // Cleanup function for when the component unmounts
    return () => {
      document.body.removeChild(script);
      if (recaptchaWidgetId.current && window.grecaptcha?.reset) {
        window.grecaptcha.reset(recaptchaWidgetId.current);
      }
    };
  }, []);

  useEffect(() => {
    if (loaded) {
      recaptchaWidgetId.current = window.grecaptcha.render(
        'recaptcha-container',
        {
          sitekey: sitekey,
          size: 'invisible',
          badge: 'bottomright',
          'expired-callback': () => setToken(null),
          'error-callback': () => setToken(null),
        },
      );
    }
  }, [loaded]);

  return (
    <ReCaptchaContext.Provider
      value={{
        token,
        loaded,
        getCaptchaToken,
      }}
    >
      {children}
      <div id="recaptcha-container" />
    </ReCaptchaContext.Provider>
  );
};

export const useReCaptcha = () => useContext(ReCaptchaContext);
